/* eslint-disable no-unused-vars -- parameter names document the vendor queue contract */
import { browser } from '$app/environment';

export type PixelLoadState = 'disabled' | 'idle' | 'loading' | 'ready' | 'error';

type TikTokQueue = unknown[][] & {
  methods: string[];
  _i: Record<string, TikTokInstance>;
  _t: Record<string, number>;
  _o: Record<string, Record<string, unknown>>;
  loaded?: boolean;
  setAndDefer: (target: TikTokInstance | TikTokQueue, method: string) => void;
  instance: (pixelId: string) => TikTokInstance;
  [method: string]: unknown;
};

type TikTokInstance = unknown[] & {
  _u?: string;
  [method: string]: unknown;
};

export interface TikTokPixel {
  init(): Promise<boolean>;
  page(): boolean;
  track(event: string, properties?: Record<string, unknown>, options?: Record<string, unknown>): boolean;
  holdConsent(): void;
  grantConsent(): void;
  revokeConsent(): void;
  getState(): PixelLoadState;
}

const SCRIPT_ID = 'quilty-tiktok-pixel';
const SCRIPT_URL = 'https://analytics.tiktok.com/i18n/pixel/events.js';
const DEFERRED_METHODS = [
  'page',
  'track',
  'identify',
  'instances',
  'debug',
  'on',
  'off',
  'once',
  'ready',
  'alias',
  'group',
  'enableCookie',
  'disableCookie',
  'holdConsent',
  'revokeConsent',
  'grantConsent',
] as const;

export function createTikTokPixel(pixelId: string): TikTokPixel {
  let state: PixelLoadState = pixelId.trim() ? 'idle' : 'disabled';
  let initPromise: Promise<boolean> | null = null;
  let lastConsent: 'hold' | 'granted' | 'denied' | null = null;

  function init(): Promise<boolean> {
    if (!browser || state === 'disabled') return Promise.resolve(false);
    if (state === 'ready') return Promise.resolve(true);
    if (initPromise) return initPromise;

    scaffoldPixel(getOrCreateQueue(), pixelId);
    setConsent('granted');
    state = 'loading';
    initPromise = loadScript(pixelId).then((loaded) => {
      state = loaded ? 'ready' : 'error';
      if (!loaded) initPromise = null;
      return loaded;
    });
    return initPromise;
  }

  function setConsent(consent: 'hold' | 'granted' | 'denied'): void {
    if (!browser || state === 'disabled' || lastConsent === consent) return;
    lastConsent = consent;
    const method = consent === 'granted'
      ? 'grantConsent'
      : consent === 'denied'
        ? 'revokeConsent'
        : 'holdConsent';
    const ttq = getOrCreateQueue();
    const deferred = ttq[method];
    if (typeof deferred === 'function') deferred();
  }

  return {
    init,
    page: () => callTikTok('page'),
    track: (event, properties = {}, options = {}) =>
      callTikTok('track', event, properties, options),
    holdConsent: () => setConsent('hold'),
    grantConsent: () => setConsent('granted'),
    revokeConsent: () => setConsent('denied'),
    getState: () => state,
  };
}

function getOrCreateQueue(): TikTokQueue {
  const existing = (window as unknown as { ttq?: TikTokQueue }).ttq;
  if (existing) return existing;

  const queue = [] as unknown as TikTokQueue;
  queue._i = {};
  queue._t = {};
  queue._o = {};
  queue.methods = [...DEFERRED_METHODS];
  queue.setAndDefer = (target, method) => {
    target[method] = (...args: unknown[]) => {
      target.push([method, ...args]);
    };
  };
  for (const method of queue.methods) {
    queue.setAndDefer(queue, method);
  }
  queue.instance = (id) => {
    const instance = queue._i[id] ?? ([] as unknown as TikTokInstance);
    for (const method of queue.methods) queue.setAndDefer(instance, method);
    return instance;
  };
  (window as unknown as { TiktokAnalyticsObject: string }).TiktokAnalyticsObject = 'ttq';
  (window as unknown as { ttq: TikTokQueue }).ttq = queue;
  return queue;
}

function scaffoldPixel(ttq: TikTokQueue, pixelId: string): void {
  if (ttq._i[pixelId]) return;
  const instance = [] as unknown as TikTokInstance;
  instance._u = SCRIPT_URL;
  for (const method of ttq.methods) ttq.setAndDefer(instance, method);
  ttq._i[pixelId] = instance;
  ttq._t[pixelId] = Date.now();
  ttq._o[pixelId] = {};
}

function callTikTok(method: 'page' | 'track', ...args: unknown[]): boolean {
  if (!browser) return false;
  const ttq = (window as unknown as { ttq?: TikTokQueue }).ttq;
  if (!ttq || typeof ttq[method] !== 'function') return false;
  try {
    ttq[method](...args);
    return true;
  } catch {
    return false;
  }
}

function loadScript(pixelId: string): Promise<boolean> {
  const src = `${SCRIPT_URL}?sdkid=${encodeURIComponent(pixelId)}&lib=ttq`;
  return new Promise((resolve) => {
    let existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing?.dataset.quiltyPixelState === 'ready' || isTikTokLoaded()) {
      if (existing) existing.dataset.quiltyPixelState = 'ready';
      resolve(true);
      return;
    }
    if (existing?.dataset.quiltyPixelState === 'error') {
      if (existing.dataset.quiltyPixelOwned === 'true') existing.remove();
      else {
        resolve(false);
        return;
      }
      existing = null;
    }
    const element = existing ?? document.createElement('script');
    element.addEventListener('load', () => {
      element.dataset.quiltyPixelState = 'ready';
      resolve(true);
    }, { once: true });
    element.addEventListener('error', () => {
      element.dataset.quiltyPixelState = 'error';
      resolve(false);
    }, { once: true });
    if (!existing) {
      element.id = SCRIPT_ID;
      element.async = true;
      element.src = src;
      element.dataset.quiltyPixelOwned = 'true';
      element.dataset.quiltyPixelState = 'loading';
      document.head.appendChild(element);
    }
  });
}

function isTikTokLoaded(): boolean {
  const ttq = (window as unknown as { ttq?: TikTokQueue }).ttq;
  return ttq?.loaded === true;
}
